import React from 'react';
import { Calendar, Clock, MapPin, FileText } from 'lucide-react';
import { Booking } from '../types';
import { BookingStatusBadge } from './BookingStatusBadge';
import { Button } from './Button';

interface BookingCardProps {
  booking: Booking;
  userRole: 'service_user' | 'provider';
  onViewDetails?: (bookingId: number) => void;
  onCancel?: (bookingId: number) => void;
  onAccept?: (bookingId: number) => void;
  onReject?: (bookingId: number) => void;
  onComplete?: (bookingId: number) => void;
  onReview?: (bookingId: number) => void;
  providerName?: string;
  userName?: string;
  hasReview?: boolean;
}

export const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  userRole,
  onViewDetails,
  onCancel,
  onAccept,
  onReject,
  onComplete,
  onReview,
  providerName,
  userName,
  hasReview = false,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isUpcoming = new Date(booking.appointmentDate) > new Date();
  const canCancel = userRole === 'service_user' && ['Pending', 'Accepted'].includes(booking.status);
  const canAccept = userRole === 'provider' && booking.status === 'Pending';
  const canReject = userRole === 'provider' && booking.status === 'Pending';
  const canComplete = userRole === 'provider' && booking.status === 'Accepted' && !isUpcoming;
  const canReview = userRole === 'service_user' && booking.status === 'Completed' && !hasReview;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">
                {userRole === 'service_user' ? providerName : userName}
              </h3>
              {userRole === 'service_user' && booking.providerIsBlocked && (
                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                  Provider unavailable
                </span>
              )}
            </div>
            <BookingStatusBadge status={booking.status} />
          </div>

          <p className="text-gray-600 mb-3">{booking.serviceType}</p>

          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {formatDate(booking.appointmentDate)}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {formatTime(booking.appointmentDate)}
            </div>
            {booking.patientSnapshot?.city && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                {booking.patientSnapshot.city}
              </div>
            )}
          </div>

          {booking.notes && (
            <div className="mt-3 flex items-start gap-2 text-sm text-gray-600">
              <FileText className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <p className="line-clamp-2">{booking.notes}</p>
            </div>
          )}

          {booking.rejectionReason && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">
                <span className="font-medium">Reason for not approving:</span> {booking.rejectionReason}
              </p>
            </div>
          )}

          {booking.patientSnapshot && userRole === 'provider' && (
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-900">
                <span className="font-medium">Patient:</span> {booking.patientSnapshot.recipientName},{' '}
                {booking.patientSnapshot.recipientAge} years old
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
        {onViewDetails && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(booking.bookingId)}
          >
            View Details
          </Button>
        )}

        {canAccept && onAccept && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onAccept(booking.bookingId)}
          >
            Approve
          </Button>
        )}

        {canReject && onReject && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onReject(booking.bookingId)}
            className="text-red-600 hover:bg-red-50"
          >
            Not Approve
          </Button>
        )}

        {canComplete && onComplete && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onComplete(booking.bookingId)}
          >
            Mark Complete
          </Button>
        )}

        {canCancel && onCancel && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onCancel(booking.bookingId)}
            className="text-red-600 hover:bg-red-50"
          >
            Cancel
          </Button>
        )}

        {canReview && onReview && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onReview(booking.bookingId)}
            className="bg-green-600 hover:bg-green-700"
          >
            Leave Review
          </Button>
        )}
      </div>
    </div>
  );
};
